import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {AwesomeBar} from "./AwesomeBar";
import {AppContainer, SettingsContainer} from "./AppContainer";
import {Launcher} from "./Launcher";
import {soundMachine} from "./SoundMachine";
import {MockTools} from "./mock/MockTools";
import {classNames, useAsync, useAsyncEffect, useFlag, useIdle} from "@variocube/cube-ui";
import {AppLauncherItem, AWESOME_BAR_HEIGHT} from "./common";
import {MockController} from "./controller/MockController";
import {StatusOverlay} from "./StatusOverlay";
import {Controller} from "./controller/Controller";
import {stylesheet} from "typestyle";
import clickSound from "./sound/click.ogg";
import background from "./img/2018-10-15-cube-wallpaper.jpg";
import {getUrlParam, getUrlParams, urlParams, wsPrefix} from "./urlParams";
import {Settings} from "./settings/Settings";
import {AppHostBridge, AppPlatform} from "./AppPlatform";
import {
    AccessCode,
    AdmissionCode,
    App,
    AppConstructor,
    Barcode,
    Language,
    LanguageListener,
    LauncherItem
} from "@variocube/app-api";
import {defined, NOP} from "@variocube/app-common";
import {theme} from "./theme";
import {ErrorBoundary} from "./ErrorBoundary";
import {logger} from "./logger";
import {safeInvokeListeners} from "./safeInvokeListeners";
import {useCurrentEventValue} from "./EventSource";
import {LocalizationProvider, SUPPORTED_LANGUAGES} from "../i18n";

const IDLE_TIMEOUT = 30;

export interface AppHostProps {
    controller: Controller;
    secondary: boolean;
}

export function AppHost({controller, secondary}: AppHostProps) {
    const {language, setLanguage, addLanguageListener, removeLanguageListener} = useLanguage();
    const {setLauncherItems, launcherItems} = useLauncherItems();
    const supportInfo = useCurrentEventValue(controller.supportInfo);

    const apps = useApps(controller, secondary, {
        setLauncherItems,
        addLanguageListener,
        removeLanguageListener,
        language,
    });

    const {launch, home, activeAppId, settings, openSettings, closeSettings} = useAppSwitching(apps);
    const availableLanguages = useAvailableLanguages(apps);
    const locationCode = useLocationCode(controller, apps, activeAppId);

    useSingleAppMode(launcherItems, launch, activeAppId);
    usePlayClick();
    useAutoReload();
    useBarcodeSupport(controller, apps, activeAppId, launch);
    useKeypadSupport(controller, apps, activeAppId, launch);

    const handleRootRef = useCallback((appId: string, rootRef: HTMLElement) => {
        apps.get(appId)?.render(rootRef);
    }, [apps]);

    const mockController = controller instanceof MockController ? controller : undefined;
    const appIds = useMemo(() => [...apps.keys()], [apps]);

    // TODO: is this necessary?
    useEffect(() => {
        console.info('Application loaded. Remove splash.');
        // remove splash
        document.getElementById('splash')?.remove();
    }, [])

    const logo = getUrlParam("awesomeBarLogo") != "disabled" && !apps.has("vsp.waste");

    return (
        <div className={classNames(theme, styles.appHost, mockController && styles.appHostMockTools)}>
            <LocalizationProvider language={language} onChangeLanguage={NOP}>
                <ErrorBoundary controller={controller}>
                    <div className={styles.appHostContent}>
                        <Launcher
                            items={launcherItems}
                            onLaunch={launch}
                        />
                        <AppContainer
                            apps={appIds}
                            active={activeAppId}
                            onRootRef={handleRootRef}
                        />
                        {settings && (
                            <SettingsContainer>
                                <Settings controller={controller} secondary={secondary} onExit={closeSettings}/>
                            </SettingsContainer>
                        )}
                    </div>
                    <AwesomeBar
                        logo={logo}
                        controller={controller}
                        onHome={home}
                        language={language}
                        availableLanguages={availableLanguages}
                        onSelectLanguage={setLanguage}
                        locationCode={locationCode}
                        onSettings={openSettings}
                        supportInfo={supportInfo}
                    />
                    {mockController && (
                        <MockTools mockController={mockController}/>
                    )}
                    <StatusOverlay controller={controller}/>
                </ErrorBoundary>
            </LocalizationProvider>
        </div>
    );
}

function useLanguage() {
    const [language, setLanguage] = useState(getInitialLanguage);
    const listeners = useMemo(() => new Set<LanguageListener>(), []);

    useEffect(() => {
        safeInvokeListeners(listeners, language);
    }, [language]);

    const addLanguageListener = useCallback((listener: LanguageListener) => listeners.add(listener), []);
    const removeLanguageListener = useCallback((listener: LanguageListener) => listeners.delete(listener), []);
    return {
        language,
        setLanguage,
        addLanguageListener,
        removeLanguageListener,
    };
}

function getInitialLanguage() {
    const locale = getUrlParam("locale") || navigator.language;
    return SUPPORTED_LANGUAGES.find(value => locale.startsWith(value.toString())) ?? SUPPORTED_LANGUAGES[0];
}


function useBarcodeSupport(controller: Controller, apps: Map<string, App>, activeAppId: string | undefined, launch: LaunchFunc) {
    const handleBarcode = useCallback((code: Barcode) => {
        logger.verbose("handleBarcode", code);
        if (activeAppId) {
            const app = apps.get(activeAppId);
            if (app && app.barcodeScanned) {
                logger.debug("handleBarcode: active app can handle barcodes", {activeAppId});
                app.barcodeScanned(code);
            }
        }
        else {
            logger.verbose("handleBarcode: No active app. Querying apps if they can read the barcode", code);
            const candidates = [...apps.entries()].reduce((acc, [id, app]) => (
                app.canReadBarcode && app.canReadBarcode(code) ? [...acc, id] : acc
            ), [] as string []);

            const selectedAppId = candidates.length == 1 ? candidates.pop() : undefined;
            if (selectedAppId) {
                const app = apps.get(selectedAppId);
                if (app && app.barcodeScanned) {
                    logger.debug("Activating app and sending barcode", selectedAppId);
                    launch(selectedAppId);
                    app.barcodeScanned(code);
                }
            }
        }
    }, [apps, activeAppId, launch]);
    useEffect(() => {
        controller.barcodeReader.addBarcodeListener(handleBarcode);
        return () => controller.barcodeReader.removeBarcodeListener(handleBarcode);
    }, [controller, handleBarcode]);
}

function useKeypadSupport(controller: Controller, apps: Map<string, App>, activeAppId: string | undefined, launch: LaunchFunc) {
    const handleAccessCode = useCallback(async (code: AccessCode) => {
        logger.verbose("handleAccessCode", code);
        if (activeAppId) {
            const app = apps.get(activeAppId);
            if (app && app.accessCodeEntered) {
                logger.debug("handleAccessCode: active app can handle access codes", {activeAppId});
                app.accessCodeEntered(code);
            }
        }
        else {
            logger.verbose("handleAccessCode: No active app. Querying apps if they can read the barcode", code);
            const candidates: string[] = [];
            for (const [id, app] of apps.entries()) {
                if (app.canProcessAccessCode && await app.canProcessAccessCode(code)) {
                    candidates.push(id)
                }
            }

            const selectedAppId = candidates.length == 1 ? candidates.pop() : undefined;
            if (selectedAppId) {
                const app = apps.get(selectedAppId);
                if (app && app.accessCodeEntered) {
                    logger.debug("Activating app and sending barcode", selectedAppId);
                    await launch(selectedAppId);
                    app.accessCodeEntered(code);
                }
            }
        }
    }, [apps, activeAppId, launch]);

    const handleAdmissionCode = useCallback(async (code: AdmissionCode) => {
        logger.verbose("handleAdmissionCode", code);
        if (activeAppId) {
            const app = apps.get(activeAppId);
            if (app && app.admissionCodeReceived) {
                logger.debug("handleAdmissionCode: active app can handle admission codes", {activeAppId});
                return await app.admissionCodeReceived(code);
            }
        } else {
            logger.verbose("handleAdmissionCode: No active app. Querying apps if they can receive the admission code", code);
            const candidates: string[] = [];
            for (const [id, app] of apps.entries()) {
                if (app.canProcessAdmissionCode && await app.canProcessAdmissionCode(code)) {
                    candidates.push(id)
                }
            }
            const selectedAppId = candidates.length == 1 ? candidates.pop() : undefined;
            if (selectedAppId) {
                const app = apps.get(selectedAppId);
                if (app && app.admissionCodeReceived) {
                    logger.debug("Activating app and sending admissionCode", selectedAppId);
                    await launch(selectedAppId);
                    return await app.admissionCodeReceived(code);
                }
            }
        }
    }, [apps, activeAppId, launch]);

    useEffect(() => {
        controller.keypad.addKeypadListener(handleAccessCode);
        controller.keypad.addAppAdmissionListener(handleAdmissionCode);
        return () => {
            controller.keypad.removeKeypadListener(handleAccessCode)
            controller.keypad.removeAppAdmissionListener(handleAdmissionCode)
        };
    }, [controller, handleAccessCode]);
}

type LaunchFunc = (appId: string, item?: LauncherItem) => Promise<void>;

function useSingleAppMode(launcherItems: AppLauncherItem[], launch: LaunchFunc, activeAppId?: string) {
    useAsyncEffect(async () => {
        if (!activeAppId && launcherItems.length == 1) {
            const item = [...launcherItems].pop();
            if (item) {
                await launch(item.appId);
            }
        }
    }, [activeAppId, launcherItems, launch]);
}

function useAppSwitching(apps: Map<string, App>) {
    const [activeAppId, setActiveAppId] = useState<string>();
    const [settings, setSettings, clearSettings] = useFlag(false);

    const idle = useIdle(IDLE_TIMEOUT);
    const [wasIdle, setWasIdle] = useState(false);

    const deactivateActiveApp = useCallback(async () => {
        try {
            clearSettings();
            if (activeAppId) {
                const activeApp = apps.get(activeAppId);
                if (activeApp) {
                    if (idle && activeApp.canAutoDeactivate) {
                        if (!activeApp.canAutoDeactivate()) {
                            throw new Error("App does not allow auto deactivate.");
                        }
                    }
                    if (activeApp.deactivate) {
                        await activeApp.deactivate();
                    }
                    logger.info("Deactivating app", activeAppId);
                    setActiveAppId(undefined);
                }
            }
        } catch (error) {
            console.error('Could not deactivate active app', error);
        }
    }, [activeAppId, apps, idle, clearSettings]);

    useAsyncEffect(async () => {
        if (idle && !wasIdle) {
            try {
                logger.info("Deactivating active app on idle.");
                await deactivateActiveApp();
            } catch (error) {
                logger.info("Cannot deactivate current app on idle.", error);
            }
            setWasIdle(true);
        }
        else if (!idle) {
            setWasIdle(false);
        }
    }, [idle, deactivateActiveApp, wasIdle]);


    const launch = useCallback(async (appId: string, item?: LauncherItem) => {
        const app = apps.get(appId);
        if (app) {
            logger.info("Launching app", {appId, item});
            if (app.activate) {
                app.activate(item);
            }
            setActiveAppId(appId);
        }
    }, [apps]);

    return useMemo(() => ({
        activeAppId,
        launch,
        home: deactivateActiveApp,
        settings,
        openSettings: setSettings,
        closeSettings: clearSettings,
    }), [activeAppId, launch, deactivateActiveApp, settings]);
}

function useLocationCode(controller: Controller, apps: Map<string, App>, activeAppId?: string) {
    const [locationCode, setLocationCode] = useState<string>(controller.locationCode);

    useEffect(() => {
        controller.addLocationCodeListener(setLocationCode);
        return () => controller.removeLocationCodeListener(setLocationCode);
    }, [controller]);

    return useMemo(() => {
        const needLocationCode = [...apps.values()].filter(app => app.supportsLocationCode).pop();
        if (!needLocationCode) {
            return undefined;
        }
        if (activeAppId) {
            const app = apps.get(activeAppId);
            if (app && app.displaysLocationCode) {
                return undefined;
            }
        }
        return locationCode;
    }, [apps, activeAppId, locationCode]);
}

function useAvailableLanguages(apps: Map<string, App>) {
    return useMemo(() => {
        const specifiedLanguages = getUrlParam("languages")?.split(",");
        const supportedLanguages = [...apps.values()]
            .map(app => app.supportedLanguages)
            .reduce((previousValue, currentValue) => [...previousValue, ...currentValue], [])
            .filter(lang => !defined(specifiedLanguages) || specifiedLanguages.indexOf(lang as Language) >= 0)
            .reduce((prev, current) => prev.add(current), new Set<string>());

        return [...supportedLanguages.values()].sort();
    }, [apps]);
}

function useLauncherItems() {
    // the main map needs to be same object over time
    const launcherItemMap = useMemo(() => new Map<string, LauncherItem[]>(), []);

    // flag that indicates a change to the map
    const [launcherItemChange, setLauncherItemChange] = useState(0);

    // update the map and flips the change flag
    const setLauncherItems = useCallback((appId: string, items: LauncherItem[]) => {
        logger.verbose("setting app launcher items", appId, items);
        launcherItemMap.set(appId, items);
        setLauncherItemChange(v => v + 1);
    }, [launcherItemMap]);

    // recompute launcher items when the flag changes
    const launcherItems = useMemo(() => {
        logger.verbose("updating launcher items");
        return [...launcherItemMap.entries()]
            .reduce((acc, [appId, items]) => {
                if (items) {
                    acc.push(...items.map(item => ({...item, appId})));
                }
                return acc;
            }, [] as AppLauncherItem[]);
    }, [launcherItemChange]);

    return {setLauncherItems, launcherItems};
}

function useApps(controller: Controller, secondary: boolean, appHostBridge: AppHostBridge) {
    const installedApps = useInstalledApps(controller);

    const [apps, setApps] = useState(() => new Map<string, App>());

    useAsyncEffect(async () => {
        const prevAppIds = new Set(apps.keys());
        const nextAppIds = new Set(installedApps);

        if (!isSameSet(prevAppIds, nextAppIds)) {
            const newApps = new Map<string, App>();
            for (const appId of installedApps) {
                try {
                    const app = apps.get(appId) ?? await loadApp(appId, controller, secondary, appHostBridge);
                    newApps.set(appId, app);
                    logger.info("Loaded app", appId);
                } catch (error) {
                    logger.error("Failed to load app", appId);
                }
            }
            setApps(newApps);
        }

    }, [installedApps]);

    return apps;
}

function isSameSet<T>(s1: Set<T>, s2: Set<T>) {
    const combined = new Set([...s1, ...s2]);
    return combined.size == s1.size && combined.size == s2.size;
}

function useInstalledApps(controller: Controller) {
    const [installedApps, setInstalledApps] = useState<string[]>([]);

    const {result} = useAsync(async () => {
        // in mock mode it is possible to override the list of apps via a URL parameter
        // in the form: ?apps=cube.service,cube.safe
        const apps = urlParams.get("apps");
        if (apps) {
            return apps.split(",");
        }
        return await controller.getApps();
    }, [controller]);

    const updateInstalledApps = useCallback((apps: string[]) => {
        // update installed apps only, if they actually changed
        const installedAppSet = new Set(installedApps);
        const newInstalledAppSet = new Set(apps);
        if (!isSameSet(installedAppSet, newInstalledAppSet)) {
            setInstalledApps(apps);
        }
    }, [installedApps]);

    useEffect(() => {
        if (result) {
            updateInstalledApps(result);
        }
    }, [result]);

    useEffect(() => {
        controller.addInstalledAppsListener(updateInstalledApps);
        return () => controller.removeInstalledAppsListener(updateInstalledApps);
    });

    return installedApps;
}

async function loadApp(id: string, controller: Controller, secondary: boolean, appHostBridge: AppHostBridge) {
    logger.info("Loading app", id);
    // load bundled app
    const module = await import(/* webpackMode: "eager" */`../../apps/${id}/src/index.tsx`);
    // default export must be an AppConstructor
    const ctor = module.default as AppConstructor;
    // create platform for app
    const platform = new AppPlatform(id, controller, getArgsForApp(id), secondary, appHostBridge);
    // create instance of app
    const instance = new ctor(platform);

    // load platform data
    await platform.load();

    if (controller instanceof MockController && instance.loadMockData) {
        logger.verbose("loading mock data", id);
        try {
            const mockStorage = await instance.loadMockData();
            if (mockStorage && typeof mockStorage == "object") {
                for (let key of Object.keys(mockStorage)) {
                    await controller.writeStorageItem(id, key, mockStorage[key]);
                }
            }
        } catch (error) {
            logger.error("Error loading mock data", error);
        }
    }

    return instance;
}

function getArgsForApp(appId: string) {
    logger.verbose("getting params of " + appId + " in ", urlParams);
    return getUrlParams(appId)
        .filter(key => key.length >= appId.length)
        .reduce((previousValue, key) => {
            return {...previousValue, [key.substr(appId.length + 1)]: getUrlParam(key)};
        }, {});
}

function usePlayClick() {
    const playClick = useCallback(() => soundMachine.sound(clickSound), []);
    useEffect(() => {
        window.addEventListener("click", playClick);
        return () => window.removeEventListener("click", playClick);
    });
}

function useAutoReload() {
    const [open, setOpen] = useState(false);
    const [wasOpen, setWasOpen] = useState(false);
    const [reload, setReload] = useState(false);
    const idle = useIdle(IDLE_TIMEOUT);

    useEffect(() => {
        const connect = () => {
            const ws = new WebSocket(`${wsPrefix()}://${location.host}/health`);
            ws.onopen = () => {
                console.info("App host health websocket open.");
                setOpen(true);
                setWasOpen(true);
            };
            ws.onclose = () => {
                console.error("App host health websocket closed.");
                setOpen(false);
                setTimeout(connect, 2500);
            };
        };
        connect();
    }, []);

    useEffect(() => {
        if (wasOpen && !open) {
            console.info("App host health websocket closed. Scheduling reload.");
            setReload(true);
        }
    }, [wasOpen, open]);

    useEffect(() => {
        if (idle && reload && open) {
            console.info("App host idle and health websocket open. Executing reload.");
            location.reload();
        }
    }, [idle, reload, open]);
}

const styles = stylesheet(({
    appHost: {
        background: `var(--color-background) url(${background}) no-repeat center center`,
        backgroundSize: "cover",
        height: "100%",
        paddingTop: AWESOME_BAR_HEIGHT,
    },
    appHostContent: {
        height: "100%",
        position: "relative",
    },
    appHostMockTools: {
        paddingBottom: AWESOME_BAR_HEIGHT,
    }
}));
